import React from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text, Button, Theme } from '../ui';
import { useNavigation } from '@react-navigation/native';

export interface TodayStep {
  outcome?: string; // Optional - backend doesn't always provide this
  action: string;
  bookTitle: string;
  bookAuthor: string;
  insightId?: string;
  bookId?: string;
}

interface TodayStepCardProps {
  step: TodayStep;
  onStart?: () => void;
  onAskMentor?: () => void;
}

/**
 * Hero card showing "Today's Step" - the core value proposition
 * Demonstrates daily action-based mentoring (not just book recommendations)
 */
export const TodayStepCard: React.FC<TodayStepCardProps> = ({ 
  step, 
  onStart, 
  onAskMentor 
}) => {
  const navigation = useNavigation();

  const handleAskMentor = () => {
    if (onAskMentor) {
      onAskMentor();
    } else {
      (navigation as any).navigate('MentorChatStack', { screen: 'MentorChat' });
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text variant="caption" style={styles.headerLabel}>TODAY'S STEP</Text>
        <View style={styles.badge}>
          <Text style={styles.badgeText}>Tailored for you</Text>
        </View>
      </View>
      
      <View style={styles.content}>
        <View style={styles.section}>
          <Text variant="heading3" style={styles.action}>
            {step.action}
          </Text>
        </View>
        
        <View style={styles.source}>
          <Text variant="caption" style={styles.sourceText}>
            From <Text style={styles.sourceBook}>{step.bookTitle.split(':')[0]}</Text> by {step.bookAuthor}
          </Text>
        </View>

        <View style={styles.actions}>
          <Button
            title="Learn more"
            onPress={onStart || (() => {})}
            style={styles.startButton}
          />
          <Pressable onPress={handleAskMentor} style={styles.askButton}>
            <Text style={styles.askButtonText}>Ask Mentor</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.colors.primary50, // Light teal background to stand out
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.lg,
    gap: Theme.spacing.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Theme.spacing.xs,
    paddingLeft: Theme.spacing.sm,
    borderLeftWidth: 4,
    borderLeftColor: Theme.colors.primary,
  },
  headerLabel: {
    color: Theme.colors.primary,
    fontWeight: '700',
    letterSpacing: 1,
  },
  badge: {
    backgroundColor: Theme.colors.primary,
    paddingHorizontal: Theme.spacing.sm,
    paddingVertical: Theme.spacing.xs,
    borderRadius: 999,
  },
  badgeText: {
    color: Theme.colors.textInverse,
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  content: {
    gap: Theme.spacing.lg,
  },
  section: {
    gap: Theme.spacing.xs,
  },
  label: {
    color: Theme.colors.textSecondary,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  outcome: {
    color: Theme.colors.textPrimary,
    lineHeight: 28,
  },
  action: {
    color: Theme.colors.textPrimary,
    lineHeight: 24,
  },
  source: {
    paddingTop: Theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: Theme.colors.borderLight,
  },
  sourceText: {
    color: Theme.colors.textSecondary,
    fontStyle: 'italic',
  },
  sourceBook: {
    color: Theme.colors.textPrimary,
    fontWeight: '600',
    fontStyle: 'normal',
  },
  actions: {
    flexDirection: 'row',
    gap: Theme.spacing.sm,
    paddingTop: Theme.spacing.md,
  },
  startButton: {
    flex: 1,
  },
  askButton: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingVertical: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.lg,
    borderRadius: Theme.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: Theme.colors.primary,
  },
  askButtonText: {
    color: Theme.colors.primary,
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
});

